import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/hooks';
import { employeeService } from '@/api/domains/employee';
import { useExport } from '@/hooks/ui/useExport';
import { useUI } from '@/hooks/ui/useUI';
import type { UIExportFormat } from '@/types/fileExport.types';
import type { EmployeeExportFilters } from '@/api/domains/employee';


export interface UseEmployeeExportParams {
  searchText?: string;
  statusFilter?: 'ALL' | 'V' | 'NV';
  contractStatusFilter?: 'ACTIVE' | 'INACTIVE' | 'ALL';
}

interface UseEmployeeExportResult {
  exportEmployees: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
}

export function useEmployeeExport(params: UseEmployeeExportParams): UseEmployeeExportResult {
  const { t: translate } = useTranslation();
  const { showNotification } = useUI();
  const companyId = useAppSelector((state) => state.auth.user?.idEmpresa);

  const { onExport: exportFile, isExporting } = useExport({
    exportFn: () => {
      const exportFilters: EmployeeExportFilters = {
        companyId: companyId!,
        searchText: params.searchText,
        statusFilter: params.statusFilter || 'ALL',
        contractStatusFilter: params.contractStatusFilter || 'ALL',
      };
      return employeeService.exportEmployees('EXCEL', exportFilters);
    },
    successMessage: translate('employee:messages.exportSuccess'),
    errorMessage: translate('employee:messages.exportError'),
  });

  const exportEmployees = useCallback(async (format: UIExportFormat) => {
    if (format !== 'EXCEL') {
      showNotification('error', translate('employee:messages.exportError'), 5000);
      return;
    }
    if (!companyId) {
      showNotification('error', translate('employee:messages.exportError'), 5000);
      return;
    }
    await exportFile();
  }, [exportFile, companyId, showNotification, translate]);

  return { exportEmployees, isExporting };
}
